// src/components/TicketDetail.tsx
import React, { useState, useEffect } from 'react';
import { mockDataService, Ticket, Customer, ConversationMessage, ApplicationEvent } from '../services/MockDataService';

interface TicketDetailProps {
  ticketId: string;
  onBack?: () => void;
  onCustomerSelect?: (phoneNumber: string) => void;
}

// Mock provider response data (since not in MockDataService yet)
interface ProviderResponse {
  provider_id: string;
  provider_name: string;
  status: 'pending' | 'accepted' | 'declined' | 'completed';
  response_time?: string;
  quote_amount?: string;
  estimated_duration?: string;
  notes?: string;
}

const TicketDetail: React.FC<TicketDetailProps> = ({ ticketId, onBack, onCustomerSelect }) => {
  const [ticket, setTicket] = useState<Ticket | null>(null);
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [messages, setMessages] = useState<ConversationMessage[]>([]);
  const [events, setEvents] = useState<ApplicationEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'overview' | 'conversation' | 'providers' | 'events'>('overview');

  // Mock provider responses for demo
  const [providerResponses] = useState<ProviderResponse[]>([
    {
      provider_id: 'provider_abc123',
      provider_name: 'Premium Plumbing Co.',
      status: 'completed',
      response_time: '15 mins',
      quote_amount: '$85-120',
      estimated_duration: '1-2 hours',
      notes: 'Can come today afternoon. Licensed and insured.'
    },
    {
      provider_id: 'provider_def456',
      provider_name: 'Quick Fix Services',
      status: 'accepted',
      response_time: '45 mins',
      quote_amount: '$75-100',
      estimated_duration: '1 hour',
      notes: 'Available tomorrow morning. Free estimates.'
    },
    {
      provider_id: 'provider_ghi789',
      provider_name: 'Elite Home Repairs',
      status: 'pending',
      response_time: '-',
      quote_amount: '-',
      estimated_duration: '-',
      notes: 'Outreach sent 2 hours ago'
    },
    {
      provider_id: 'provider_jkl012',
      provider_name: 'Budget Plumbers',
      status: 'declined',
      response_time: '2 hours',
      quote_amount: '-',
      estimated_duration: '-',
      notes: 'Too busy this week'
    }
  ]);

  useEffect(() => {
    const loadTicketData = async () => {
      setLoading(true);
      try {
        const ticketData = await mockDataService.getTicket(ticketId);
        if (!ticketData) return;

        const [customerData, messagesData, eventsData] = await Promise.all([
          mockDataService.getCustomer(ticketData.phone_number),
          mockDataService.getConversationMessages(ticketData.conversation_id),
          mockDataService.getEvents(ticketData.phone_number, 20)
        ]);
        
        setTicket(ticketData);
        setCustomer(customerData);
        setMessages(messagesData);
        // Filter events related to this ticket
        setEvents(eventsData.filter(e => e.ticket_id === ticketId));
      } catch (error) {
        console.error('Error loading ticket data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadTicketData();
  }, [ticketId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading ticket details...</p>
        </div>
      </div>
    );
  }

  if (!ticket) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <span className="text-6xl mb-4 block">🎫</span>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Ticket Not Found</h2>
          <p className="text-gray-600 mb-4">No ticket found with ID: {ticketId}</p>
          <button 
            onClick={onBack}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md"
          >
            ← Back
          </button>
        </div>
      </div>
    );
  }

  const getTicketStatusBadge = (status: Ticket['status']) => {
    const styles = {
      draft: 'bg-gray-100 text-gray-800',
      open: 'bg-blue-100 text-blue-800',
      in_progress: 'bg-yellow-100 text-yellow-800',
      fulfilled: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800'
    };

    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${styles[status]}`}>
        {status.replace('_', ' ').toUpperCase()}
      </span>
    );
  };

  const getProviderStatusBadge = (status: ProviderResponse['status']) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-800',
      accepted: 'bg-blue-100 text-blue-800',
      declined: 'bg-red-100 text-red-800',
      completed: 'bg-green-100 text-green-800'
    };

    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${styles[status]}`}>
        {status.toUpperCase()}
      </span>
    );
  };

  const getEventStatusIcon = (status: ApplicationEvent['status']) => {
    const icons = {
      success: '✅',
      failed: '❌',
      pending: '⏳',
      in_progress: '🔄'
    };
    return icons[status] || '❓';
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatEventType = (eventType: string) => {
    return eventType.split('_').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-4">
            <div className="flex items-center">
              <button 
                onClick={onBack}
                className="mr-4 text-gray-500 hover:text-gray-700"
              >
                ← Back
              </button>
              <h1 className="text-2xl font-bold text-gray-900">
                🎫 Ticket Detail
              </h1>
            </div>
            <div className="text-sm text-gray-500">
              Created: {formatDate(ticket.created_at)}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        
        {/* Ticket Header Card */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-2">{ticket.ticket_id}</h2>
              <div className="flex items-center space-x-3 mb-3">
                {getTicketStatusBadge(ticket.status)}
                {ticket.urgency_level && (
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-800">
                    🚨 URGENT
                  </span>
                )}
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-purple-100 text-purple-800">
                  {ticket.service_type.replace('_', ' ').toUpperCase()}
                </span>
              </div>
            </div>

            <div className="text-right">
              <div className="text-sm font-medium text-gray-900 mb-1">
                {ticket.completion_percentage}% Complete
              </div>
              <div className="w-32 bg-gray-200 rounded-full h-3">
                <div 
                  className="bg-blue-600 h-3 rounded-full transition-all duration-300" 
                  style={{ width: `${ticket.completion_percentage}%` }}
                ></div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <h4 className="font-medium text-gray-700 mb-2">Customer</h4>
              <button 
                onClick={() => onCustomerSelect?.(ticket.phone_number)}
                className="text-blue-600 hover:text-blue-800 font-medium"
              >
                {ticket.phone_number} →
              </button>
              {customer && (
                <p className="text-sm text-gray-600 mt-1">
                  Status: {customer.customer_status.replace('_', ' ')}
                </p>
              )}
            </div>

            <div>
              <h4 className="font-medium text-gray-700 mb-2">Service Request</h4>
              <p className="text-sm text-gray-900">{ticket.issue_description}</p>
            </div>

            <div>
              <h4 className="font-medium text-gray-700 mb-2">Key Details</h4>
              <dl className="text-sm space-y-1">
                <div>
                  <dt className="inline text-gray-600">Service: </dt>
                  <dd className="inline font-medium">{ticket.service_type.replace('_', ' ')}</dd>
                </div>
                <div>
                  <dt className="inline text-gray-600">Created: </dt>
                  <dd className="inline font-medium">{formatDate(ticket.created_at)}</dd>
                </div>
                {ticket.selected_provider_id && (
                  <div>
                    <dt className="inline text-gray-600">Selected Provider: </dt>
                    <dd className="inline font-medium">{ticket.selected_provider_id}</dd>
                  </div>
                )}
              </dl>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8 px-6">
              {(['overview', 'conversation', 'providers', 'events'] as const).map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`py-4 px-1 border-b-2 font-medium text-sm ${
                    activeTab === tab
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  {tab.charAt(0).toUpperCase() + tab.slice(1)}
                  {tab === 'conversation' && ` (${messages.length})`}
                  {tab === 'providers' && ` (${providerResponses.length})`}
                  {tab === 'events' && ` (${events.length})`}
                </button>
              ))}
            </nav>
          </div>

          <div className="p-6">
            {/* Overview Tab */}
            {activeTab === 'overview' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Extracted Fields</h3>
                  {Object.keys(ticket.extracted_fields).length === 0 ? (
                    <p className="text-gray-500 italic">No extracted fields available.</p>
                  ) : (
                    <div className="bg-gray-50 rounded-lg p-4">
                      <dl className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {Object.entries(ticket.extracted_fields).map(([key, value]) => (
                          <div key={key}>
                            <dt className="text-sm font-medium text-gray-700">
                              {key.replace('_', ' ').toUpperCase()}
                            </dt>
                            <dd className="text-sm text-gray-900 mt-1">{value}</dd>
                          </div>
                        ))}
                      </dl>
                    </div>
                  )}
                </div>

                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Ticket Summary</h3>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <p className="text-sm text-blue-900">{ticket.summary_text || ticket.issue_description}</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-medium text-gray-700 mb-2">Conversation</h4>
                    <p className="text-sm text-gray-600">ID: {ticket.conversation_id}</p>
                    <p className="text-sm text-gray-600">Messages: {messages.length}</p>
                  </div>

                  <div>
                    <h4 className="font-medium text-gray-700 mb-2">Provider Matching</h4>
                    <p className="text-sm text-gray-600">
                      LLM Rankings: {ticket.llm_ranked_providers.length}
                    </p>
                    <p className="text-sm text-gray-600">
                      Selected: {ticket.selected_provider_id || 'None'}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Conversation Tab */}
            {activeTab === 'conversation' && (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-gray-900">Conversation History</h3>
                  <span className="text-sm text-gray-600">{messages.length} messages</span>
                </div>

                {messages.length === 0 ? (
                  <div className="text-center py-8">
                    <span className="text-4xl mb-4 block">💬</span>
                    <p className="text-gray-500">No conversation messages found.</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {messages.map((message) => (
                      <div
                        key={message.message_id}
                        className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                      >
                        <div
                          className={`max-w-md px-4 py-3 rounded-lg ${
                            message.role === 'user'
                              ? 'bg-blue-600 text-white'
                              : message.role === 'Lookey'
                              ? 'bg-gray-100 text-gray-900'
                              : 'bg-purple-100 text-purple-900'
                          }`}
                        >
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-xs font-medium opacity-75">
                              {message.role === 'user' ? 'Customer' : message.role}
                            </span>
                            <span className="text-xs opacity-75">
                              {formatDate(message.timestamp)}
                            </span>
                          </div>
                          <p className="text-sm">{message.content}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Providers Tab */}
            {activeTab === 'providers' && (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-gray-900">Provider Responses</h3>
                  <span className="text-sm text-gray-600">{providerResponses.length} providers contacted</span>
                </div>

                <div className="space-y-4">
                  {providerResponses.map((provider) => (
                    <div key={provider.provider_id} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-3 mb-2">
                            <h4 className="font-medium text-gray-900">{provider.provider_name}</h4>
                            {getProviderStatusBadge(provider.status)}
                            {ticket.selected_provider_id === provider.provider_id && (
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                ⭐ SELECTED
                              </span>
                            )}
                          </div>
                          
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                            <div>
                              <span className="font-medium text-gray-700">Response Time:</span>
                              <p className="text-gray-900">{provider.response_time}</p>
                            </div>
                            <div>
                              <span className="font-medium text-gray-700">Quote:</span>
                              <p className="text-gray-900">{provider.quote_amount}</p>
                            </div>
                            <div>
                              <span className="font-medium text-gray-700">Duration:</span>
                              <p className="text-gray-900">{provider.estimated_duration}</p>
                            </div>
                          </div>

                          {provider.notes && (
                            <div className="mt-3 p-3 bg-gray-50 rounded-md">
                              <p className="text-sm text-gray-700">
                                <span className="font-medium">Notes:</span> {provider.notes}
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-6 grid grid-cols-1 md:grid-cols-4 gap-4 text-center">
                  <div>
                    <div className="text-2xl font-bold text-green-600">
                      {providerResponses.filter(p => p.status === 'completed').length}
                    </div>
                    <div className="text-sm text-gray-600">Completed</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-blue-600">
                      {providerResponses.filter(p => p.status === 'accepted').length}
                    </div>
                    <div className="text-sm text-gray-600">Accepted</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-yellow-600">
                      {providerResponses.filter(p => p.status === 'pending').length}
                    </div>
                    <div className="text-sm text-gray-600">Pending</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-red-600">
                      {providerResponses.filter(p => p.status === 'declined').length}
                    </div>
                    <div className="text-sm text-gray-600">Declined</div>
                  </div>
                </div>
              </div>
            )}

            {/* Events Tab */}
            {activeTab === 'events' && (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-gray-900">Ticket Events</h3>
                  <span className="text-sm text-gray-600">{events.length} events</span>
                </div>

                {events.length === 0 ? (
                  <div className="text-center py-8">
                    <span className="text-4xl mb-4 block">📅</span>
                    <p className="text-gray-500">No events found for this ticket.</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {events.map((event, index) => (
                      <div key={`${event.event_timestamp}-${index}`} className="flex items-start space-x-3 pb-3 border-b border-gray-100 last:border-b-0">
                        <div className="flex-shrink-0 text-lg">
                          {getEventStatusIcon(event.status)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <p className="text-sm font-medium text-gray-900">
                              {formatEventType(event.event_type)}
                            </p>
                            <p className="text-xs text-gray-500">
                              {formatDate(event.created_at)}
                            </p>
                          </div>
                          <div className="flex items-center space-x-2 mt-1">
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                              event.status === 'success' ? 'bg-green-100 text-green-800' :
                              event.status === 'failed' ? 'bg-red-100 text-red-800' :
                              event.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                              'bg-blue-100 text-blue-800'
                            }`}>
                              {event.status.toUpperCase()}
                            </span>
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                              {event.user_type.toUpperCase()}
                            </span>
                            {event.provider_id && (
                              <span className="text-xs text-gray-600">
                                Provider: {event.provider_id}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

      </div>
    </div>
  );
};

export default TicketDetail;